import { useAuth } from '@clerk/clerk-react';
import { useState, useCallback } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export enum JobTrackingStatus {
  SAVED = 'SAVED',
  APPLIED = 'APPLIED',
  INTERVIEW = 'INTERVIEW',
  OFFER = 'OFFER',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
  WITHDRAWN = 'WITHDRAWN',
}

export interface TrackedJob {
  id: string;
  userId: string;
  jobId: string;
  status: JobTrackingStatus;
  notes?: string;
  appliedDate?: string;
  createdAt: string;
  updatedAt: string;
  job: {
    id: string;
    title: string;
    description: string;
    location: string;
    locationType: string;
    salaryMin?: number;
    salaryMax?: number;
    salaryCurrency: string;
    equityOffered: boolean;
    experienceLevel?: string;
    visaSponsorship: boolean;
    techStack: string[];
    sourceUrl: string;
    sourceType: string;
    applyUrl?: string;
    postedAt: string;
    company: {
      id: string;
      name: string;
      slug: string;
      logoUrl?: string;
      averageRating?: number;
      reviewCount: number;
    };
  };
}

export interface PaginatedTrackedJobsResponse {
  data: TrackedJob[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface TrackingStats {
  total: number;
  byStatus: Record<JobTrackingStatus, number>;
}

export function useTrackedJobs() {
  const [trackedJobs, setTrackedJobs] = useState<TrackedJob[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { getToken } = useAuth();

  const fetchTrackedJobs = useCallback(
    async (
      page: number = 1,
      limit: number = 25,
      status?: JobTrackingStatus,
      sortBy?: string,
      sortOrder?: 'asc' | 'desc'
    ) => {
      setIsLoading(true);
      setError(null);

      try {
        const token = await getToken();
        let url = `${API_URL}/tracked-jobs?page=${page}&limit=${limit}`;
        if (status) {
          url += `&status=${status}`;
        }
        if (sortBy) {
          url += `&sortBy=${sortBy}`;
        }
        if (sortOrder) {
          url += `&sortOrder=${sortOrder}`;
        }

        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch tracked jobs');
        }

        const result: PaginatedTrackedJobsResponse = await response.json();
        setTrackedJobs(result.data);
        setPagination({
          page: result.page,
          limit: result.limit,
          total: result.total,
          totalPages: result.totalPages,
        });
        return result;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to fetch tracked jobs';
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [getToken]
  );

  const trackJob = useCallback(
    async (jobId: string, status: JobTrackingStatus = JobTrackingStatus.SAVED) => {
      try {
        const token = await getToken();
        const response = await fetch(`${API_URL}/tracked-jobs`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ jobId, status }),
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || 'Failed to track job');
        }

        return await response.json();
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to track job';
        throw new Error(errorMessage);
      }
    },
    [getToken]
  );

  const updateStatus = useCallback(
    async (trackingId: string, status: JobTrackingStatus, refetchFn?: () => Promise<any>) => {
      try {
        const token = await getToken();
        const response = await fetch(`${API_URL}/tracked-jobs/${trackingId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ status }),
        });

        if (!response.ok) {
          throw new Error('Failed to update status');
        }

        // Update local state immediately
        setTrackedJobs((prev) =>
          prev.map((job) => (job.id === trackingId ? { ...job, status } : job))
        );

        const result = await response.json();

        // Refetch if custom function provided
        if (refetchFn) {
          await refetchFn();
        }

        return result;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to update status';
        // Refetch to restore state if custom function provided
        if (refetchFn) {
          await refetchFn();
        }
        throw new Error(errorMessage);
      }
    },
    [getToken]
  );

  const untrackJob = useCallback(
    async (trackingId: string, refetchFn?: () => Promise<any>) => {
      try {
        const token = await getToken();
        const response = await fetch(`${API_URL}/tracked-jobs/${trackingId}`, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to remove job from tracking');
        }

        // Use custom refetch if provided, otherwise use default
        if (refetchFn) {
          await refetchFn();
        } else {
          await fetchTrackedJobs(pagination.page);
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to remove job from tracking';
        throw new Error(errorMessage);
      }
    },
    [getToken, fetchTrackedJobs, pagination.page]
  );

  return {
    trackedJobs,
    pagination,
    fetchTrackedJobs,
    trackJob,
    updateStatus,
    untrackJob,
    isLoading,
    error,
  };
}

export function useTrackingStats() {
  const [stats, setStats] = useState<TrackingStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { getToken } = useAuth();

  const fetchStats = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/tracked-jobs/stats`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch tracking stats');
      }

      const result: TrackingStats = await response.json();
      setStats(result);
      return result;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch stats';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [getToken]);

  return {
    stats,
    fetchStats,
    isLoading,
    error,
  };
}
